import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Schedule {
  id: string;
  business_id: string;
  display_id: string | null;
  name: string;
  playlist_id: string | null;
  content_item_id: string | null;
  start_time: string; // TIME format: "HH:MM:SS"
  end_time: string;
  days_of_week: number[]; // 0=Sunday, 6=Saturday
  start_date: string | null;
  end_date: string | null;
  priority: number;
  is_active: boolean;
  created_at: string;
  // Joined data
  playlist?: {
    id: string;
    name: string;
  } | null;
  content_item?: {
    id: string;
    name: string;
    content_type: string;
  } | null;
  display?: {
    id: string;
    name: string;
  } | null;
}

export interface CreateScheduleInput {
  name: string;
  display_id?: string | null;
  playlist_id?: string | null;
  content_item_id?: string | null;
  start_time: string;
  end_time: string;
  days_of_week?: number[];
  start_date?: string | null;
  end_date?: string | null;
  priority?: number;
}

export interface UpdateScheduleInput {
  name?: string;
  display_id?: string | null;
  playlist_id?: string | null;
  content_item_id?: string | null;
  start_time?: string;
  end_time?: string;
  days_of_week?: number[];
  start_date?: string | null;
  end_date?: string | null;
  priority?: number;
  is_active?: boolean;
}

export function useSignageSchedules(businessId: string) {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedules = useCallback(async () => {
    if (!businessId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('signage_schedules')
        .select(`
          *,
          signage_playlists (
            id,
            name
          ),
          signage_content_items (
            id,
            name,
            content_type
          ),
          displays (
            id,
            name
          )
        `)
        .eq('business_id', businessId)
        .order('priority', { ascending: false })
        .order('start_time', { ascending: true });

      if (fetchError) throw fetchError;

      const transformedSchedules: Schedule[] = (data || []).map((s: any) => ({
        id: s.id,
        business_id: s.business_id,
        display_id: s.display_id,
        name: s.name,
        playlist_id: s.playlist_id,
        content_item_id: s.content_item_id,
        start_time: s.start_time,
        end_time: s.end_time,
        days_of_week: s.days_of_week || [0, 1, 2, 3, 4, 5, 6],
        start_date: s.start_date,
        end_date: s.end_date,
        priority: s.priority || 0,
        is_active: s.is_active,
        created_at: s.created_at,
        playlist: s.signage_playlists,
        content_item: s.signage_content_items,
        display: s.displays,
      }));

      setSchedules(transformedSchedules);
    } catch (err: any) {
      console.error('Failed to fetch schedules:', err);
      setError(err.message);
      toast.error('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const createSchedule = useCallback(async (input: CreateScheduleInput): Promise<Schedule | null> => {
    try {
      // Validate that either playlist_id or content_item_id is provided
      if (!input.playlist_id && !input.content_item_id) {
        toast.error('Please select a playlist or content item');
        return null;
      }

      const { data, error } = await supabase
        .from('signage_schedules')
        .insert({
          business_id: businessId,
          name: input.name,
          display_id: input.display_id || null,
          playlist_id: input.playlist_id || null,
          content_item_id: input.content_item_id || null,
          start_time: input.start_time,
          end_time: input.end_time,
          days_of_week: input.days_of_week || [0, 1, 2, 3, 4, 5, 6],
          start_date: input.start_date || null,
          end_date: input.end_date || null,
          priority: input.priority || 0,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Schedule created');
      await fetchSchedules();
      return data as Schedule;
    } catch (err: any) {
      console.error('Failed to create schedule:', err);
      toast.error('Failed to create schedule');
      return null;
    }
  }, [businessId, fetchSchedules]);

  const updateSchedule = useCallback(async (
    scheduleId: string,
    input: UpdateScheduleInput
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('signage_schedules')
        .update(input)
        .eq('id', scheduleId);

      if (error) throw error;

      toast.success('Schedule updated');
      await fetchSchedules();
      return true;
    } catch (err: any) {
      console.error('Failed to update schedule:', err);
      toast.error('Failed to update schedule');
      return false;
    }
  }, [fetchSchedules]);

  const deleteSchedule = useCallback(async (scheduleId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('signage_schedules')
        .delete()
        .eq('id', scheduleId);

      if (error) throw error;

      toast.success('Schedule deleted');
      await fetchSchedules();
      return true;
    } catch (err: any) {
      console.error('Failed to delete schedule:', err);
      toast.error('Failed to delete schedule');
      return false;
    }
  }, [fetchSchedules]);

  const toggleScheduleActive = useCallback(async (
    scheduleId: string,
    isActive: boolean
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('signage_schedules')
        .update({ is_active: isActive })
        .eq('id', scheduleId);

      if (error) throw error;

      toast.success(isActive ? 'Schedule activated' : 'Schedule deactivated');
      await fetchSchedules();
      return true;
    } catch (err: any) {
      console.error('Failed to toggle schedule:', err);
      toast.error('Failed to update schedule');
      return false;
    }
  }, [fetchSchedules]);

  const duplicateSchedule = useCallback(async (scheduleId: string): Promise<Schedule | null> => {
    const schedule = schedules.find(s => s.id === scheduleId);
    if (!schedule) {
      toast.error('Schedule not found');
      return null;
    }

    return createSchedule({
      name: `${schedule.name} (Copy)`,
      display_id: schedule.display_id,
      playlist_id: schedule.playlist_id,
      content_item_id: schedule.content_item_id,
      start_time: schedule.start_time,
      end_time: schedule.end_time,
      days_of_week: schedule.days_of_week,
      start_date: schedule.start_date,
      end_date: schedule.end_date,
      priority: schedule.priority,
    });
  }, [schedules, createSchedule]);

  return {
    schedules,
    loading,
    error,
    fetchSchedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    toggleScheduleActive,
    duplicateSchedule,
  };
}

// Helper functions for schedule time logic
export function formatTime(time: string): string {
  // Convert "HH:MM:SS" to "HH:MM AM/PM"
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
}

export function formatTimeRange(startTime: string, endTime: string): string {
  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
}

export function getDayNames(days: number[]): string {
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (days.length === 7) return 'Every day';
  if (arraysEqual(days.sort(), [1, 2, 3, 4, 5])) return 'Weekdays';
  if (arraysEqual(days.sort(), [0, 6])) return 'Weekends';

  return days.map(d => dayNames[d]).join(', ');
}

function arraysEqual(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function isScheduleActive(schedule: Schedule, date: Date = new Date()): boolean {
  if (!schedule.is_active) return false;

  // Check date range
  if (schedule.start_date && new Date(schedule.start_date) > date) return false;
  if (schedule.end_date && new Date(schedule.end_date) < date) return false;

  // Check day of week
  const dayOfWeek = date.getDay();
  if (!schedule.days_of_week.includes(dayOfWeek)) return false;

  // Check time range
  const currentTime = date.toTimeString().slice(0, 8); // "HH:MM:SS"

  // Handle overnight schedules (e.g., 22:00 - 06:00)
  if (schedule.start_time > schedule.end_time) {
    return currentTime >= schedule.start_time || currentTime < schedule.end_time;
  }

  return currentTime >= schedule.start_time && currentTime < schedule.end_time;
}
